import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, IconButton, CircularProgress, Chip, Autocomplete, Checkbox, Tooltip,
  Collapse, Radio, RadioGroup, FormControlLabel, Link, Stack, Alert
} from '@mui/material';
import {
  Delete as DeleteIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Save as SaveIcon,
  TravelExplore as SurfIcon,
  OpenInNew as OpenInNewIcon,
  ChangeHistory as FlagTriangleIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { ApiService } from '../services/api';

// Newsletter targeting is defined at the company/domain level and maps to company.type_.
// These are the only valid newsletter tokens; the column is an editable multiselect.
const NEWSLETTERS = ['THC', 'CBD', 'INV', 'MED'];
const PAGE_SIZE = 5;

interface SearchListItem {
  id: number;
  domain_id: number;
  business_name: string;
  website: string;
  clean_name: string | null;
  name: string | null;
  state_name: string | null;
  flagged: boolean;
  search_query: string;
  url: string | null;
  title: string | null;
  description: string | null;
  tag1: string | null;
  tag2: string | null;
  tag3: string | null;
  tag4: string | null;
  newsletters: string[];
  human_override: boolean;
  llm_status: string;
  uncertainty_score: number;
  in_mailer_count: number;
}

interface Tag {
  id: number;
  name: string;
  category?: string;
}

interface SearchResult {
  url: string;
  title: string;
  description: string;
  displayLink: string;
  tag: string;
  existsInDb: boolean;
}

// Default "save for later" list name = a timestamp, editable by the user.
const defaultSaveList = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `Saved ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Per-row "Surf" search state, keyed by row id.
interface RowSearch {
  loading: boolean;
  error?: string;
  results: SearchResult[];
  hasMore: boolean;
  picked: number | 'other' | null; // index into results, 'other', or none
  otherUrl: string;
}

// Best human-readable name for a company row.
const displayName = (item: Partial<SearchListItem>) =>
  (item.business_name || item.clean_name || item.name || '').trim();

const OutreachSearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const listName = searchParams.get('list') || '';

  const [items, setItems] = useState<SearchListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [globalKeywords, setGlobalKeywords] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const [tags, setTags] = useState<Tag[]>([]);

  const [rowSearch, setRowSearch] = useState<Record<number, RowSearch>>({});
  const [surfing, setSurfing] = useState(false);

  // "Save for later" list: a timestamp-named bucket of flagged web results.
  const [saveListName, setSaveListName] = useState<string>(defaultSaveList());
  const [savedUrls, setSavedUrls] = useState<Set<string>>(new Set());

  // Deep Search: when enabled, fetch 100 results (but show 5); capture all unique domains for later evaluation.
  const [deepSearch, setDeepSearch] = useState(false);
  const [deepSearchCount, setDeepSearchCount] = useState(0);

  // Track which rows are currently saving (for save-on-blur visual feedback)
  const [savingRows, setSavingRows] = useState<Set<number>>(new Set());
  const [pageAlert, setPageAlert] = useState<{ severity: 'error' | 'success' | 'warning' | 'info'; message: string } | null>(null);
  const [sendingOutreach, setSendingOutreach] = useState(false);
  const [geminiConfigured, setGeminiConfigured] = useState<boolean | null>(null);

  const saveRowField = async (id: number, field: keyof SearchListItem, value: any) => {
    setSavingRows(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    try {
      const item = items.find(i => i.id === id);
      if (!item) return;
      const updated = { ...item, [field]: value };
      await ApiService.updateSearchListBulk([updated]);
    } catch (err) {
      console.error(`Failed to save ${field}:`, err);
    } finally {
      setSavingRows(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, tagsRes, llmRes] = await Promise.all([
        ApiService.getSearchList(listName || undefined),
        ApiService.getIndustryTags(),
        ApiService.getLlmSettings().catch(() => null),
      ]);
      setGeminiConfigured(Boolean(llmRes?.gemini_api_key));
      setItems(listRes.map((r: any) => {
        const newsletters = typeof r.newsletters === 'string'
          ? JSON.parse(r.newsletters || '[]')
          : (r.newsletters || []);
        const item = { ...r, newsletters, human_override: !!r.human_override, flagged: !!r.flagged } as SearchListItem;
        // Prefill the query box with the company name when empty.
        if (!item.search_query) item.search_query = displayName(item);
        return item;
      }));
      // getIndustryTags returns { data: [...all tags...] }, not a bare array.
      const tagList: Tag[] = Array.isArray(tagsRes) ? tagsRes : (tagsRes?.data || []);
      setTags(tagList);
      setSelected(new Set());
      setRowSearch({});
    } catch (err: any) {
      console.error(err);
      setPageAlert({ severity: 'error', message: err?.message || 'Failed to load search list' });
    } finally {
      setLoading(false);
    }
  }, [listName]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Append the global keyword to every row's query (don't clobber the name; dedupe).
  const handleApplyGlobalKeywords = () => {
    const kw = globalKeywords.trim();
    if (!kw) return;
    setItems(prev => prev.map(item => {
      const base = item.search_query || displayName(item);
      const already = base.toLowerCase().split(/\s+/).includes(kw.toLowerCase());
      return { ...item, search_query: already ? base : `${base} ${kw}`.trim() };
    }));
  };

  const handleUpdateItem = (id: number, field: keyof SearchListItem, value: any) => {
    setItems(prev => prev.map(it => (it.id === id ? { ...it, [field]: value } : it)));
  };

  const handleUpdateTags = (id: number, newTags: Tag[]) => {
    setItems(prev => prev.map(it => it.id === id ? {
      ...it,
      tag1: newTags[0]?.name || null,
      tag2: newTags[1]?.name || null,
      tag3: newTags[2]?.name || null,
      tag4: newTags[3]?.name || null,
    } : it));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await ApiService.updateSearchListBulk(items);
      setPageAlert({ severity: 'success', message: 'Changes saved.' });
    } catch (err: any) {
      console.error(err);
      setPageAlert({ severity: 'error', message: err?.message || 'Failed to save changes' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRow = async (id: number) => {
    if (!window.confirm('Remove this company from the search list?')) return;
    try {
      await ApiService.deleteSearchListItems([id]);
      setItems(prev => prev.filter(i => i.id !== id));
      setSelected(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err: any) {
      console.error(err);
      setPageAlert({ severity: 'error', message: err?.message || 'Failed to delete row' });
    }
  };

  const handleSendToOutreach = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Send ${ids.length} selected ${ids.length === 1 ? 'company' : 'companies'} to Outreach? They will be removed from this search list.`)) {
      return;
    }
    setSendingOutreach(true);
    try {
      await ApiService.updateSearchListBulk(items.filter(i => selected.has(i.id)));
      const result = await ApiService.sendSearchToOutreach(ids);
      if (result?.sessionId) {
        navigate(`/outreach/domain-list?session=${result.sessionId}`);
      } else {
        throw new Error(result?.message || 'Send to Outreach did not return a session.');
      }
    } catch (err: any) {
      console.error(err);
      setPageAlert({ severity: 'error', message: err?.message || 'Failed to send to Outreach' });
    } finally {
      setSendingOutreach(false);
    }
  };

  // --- Row selection (leftmost column) ---
  const allSelected = items.length > 0 && selected.size === items.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(items.map(i => i.id)));
  };
  const toggleRow = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // --- Surf: run a Google search per selected row, one at a time ---
  const runSearchForRow = async (item: SearchListItem, append = false) => {
    const query = (item.search_query || displayName(item)).trim();
    if (!query) return;
    const existing = rowSearch[item.id];
    // On "More", exclude the URLs already shown so we get fresh ones.
    const exclude = append && existing ? existing.results.map(r => r.url) : [];
    setRowSearch(prev => ({
      ...prev,
      [item.id]: {
        ...(prev[item.id] || { results: [], hasMore: false, picked: null, otherUrl: '' }),
        loading: true,
        error: undefined,
      } as RowSearch,
    }));
    try {
      const res = await ApiService.googleResults(query, PAGE_SIZE, exclude, deepSearch);
      setRowSearch(prev => {
        const cur = prev[item.id];
        const merged = append && cur ? [...cur.results, ...res.results] : res.results;
        return {
          ...prev,
          [item.id]: {
            loading: false,
            results: merged,
            hasMore: res.hasMore,
            picked: cur?.picked ?? null,
            otherUrl: cur?.otherUrl ?? '',
          },
        };
      });

      // If deep search, capture all 100 domains to the deep search list
      if (deepSearch && res.allResults && listName) {
        try {
          await ApiService.saveDeepSearchResults(listName, query, res.allResults);
          setDeepSearchCount(prev => prev + (res.deepSearchCount || 0));
        } catch (dsErr) {
          console.error('Failed to save deep search results:', dsErr);
        }
      }
    } catch (err: any) {
      setRowSearch(prev => ({
        ...prev,
        [item.id]: {
          loading: false,
          results: prev[item.id]?.results || [],
          hasMore: false,
          picked: prev[item.id]?.picked ?? null,
          otherUrl: prev[item.id]?.otherUrl ?? '',
          error: err?.message || 'Search failed',
        },
      }));
    }
  };

  // Flag/unflag a single web result into the "save for later" list (persists immediately).
  const toggleSaveResult = async (item: SearchListItem, r: SearchResult) => {
    const isSaved = savedUrls.has(r.url);
    setSavedUrls(prev => {
      const n = new Set(prev);
      isSaved ? n.delete(r.url) : n.add(r.url);
      return n;
    });
    try {
      if (isSaved) {
        await ApiService.removeSearchResult(saveListName, r.url);
      } else {
        await ApiService.saveSearchResult({
          list_name: saveListName,
          source_query: item.search_query || displayName(item),
          url: r.url,
          title: r.title,
          description: r.description,
          source_domain_id: item.domain_id,
        });
      }
    } catch (err) {
      console.error('Failed to toggle saved result', err);
      // revert on failure
      setSavedUrls(prev => {
        const n = new Set(prev);
        isSaved ? n.add(r.url) : n.delete(r.url);
        return n;
      });
    }
  };

  const handleSurf = async () => {
    const targets = items.filter(i => selected.has(i.id));
    if (targets.length === 0) return;
    setSurfing(true);
    try {
      // Sequential so results accordion down one-by-one as each finishes.
      for (const item of targets) {
        await runSearchForRow(item, false);
      }
    } finally {
      setSurfing(false);
    }
  };

  // Pick a result (or "other"/none) for a row and write the chosen url/title/description.
  const pickResult = (item: SearchListItem, picked: number | 'other' | null) => {
    setRowSearch(prev => ({ ...prev, [item.id]: { ...prev[item.id], picked } }));
    if (picked === null) {
      handleUpdateItem(item.id, 'url', null);
      handleUpdateItem(item.id, 'title', null);
      handleUpdateItem(item.id, 'description', null);
    } else if (picked === 'other') {
      handleUpdateItem(item.id, 'url', rowSearch[item.id]?.otherUrl || '');
      handleUpdateItem(item.id, 'title', null);
      handleUpdateItem(item.id, 'description', null);
    } else {
      const r = rowSearch[item.id]?.results[picked];
      if (r) {
        handleUpdateItem(item.id, 'url', r.url);
        handleUpdateItem(item.id, 'title', r.title);
        handleUpdateItem(item.id, 'description', r.description);
      }
    }
  };

  const setOtherUrl = (item: SearchListItem, value: string) => {
    setRowSearch(prev => ({ ...prev, [item.id]: { ...prev[item.id], otherUrl: value } }));
    if (rowSearch[item.id]?.picked === 'other') {
      handleUpdateItem(item.id, 'url', value);
    }
  };

  const itemTagObjects = (item: SearchListItem): Tag[] =>
    [item.tag1, item.tag2, item.tag3, item.tag4]
      .filter(Boolean)
      .map((n, i) => tags.find(t => t.name === n) || { id: -1 - i, name: n as string });

  return (
    <Box sx={{ p: 2, maxWidth: 1700, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Web Search</Typography>
          {listName && (
            <Typography variant="caption" color="text.secondary">
              List: <strong>{listName}</strong> · {items.length} {items.length === 1 ? 'company' : 'companies'}
              {selected.size > 0 ? ` · ${selected.size} selected` : ''}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={surfing ? <CircularProgress size={16} color="inherit" /> : <SurfIcon />}
            onClick={handleSurf}
            disabled={surfing || loading || selected.size === 0}
          >
            {surfing ? 'Surfing...' : `Surf${selected.size ? ` (${selected.size})` : ''}`}
          </Button>
          <Button
            variant="contained"
            size="small"
            color="secondary"
            startIcon={sendingOutreach ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            onClick={handleSendToOutreach}
            disabled={sendingOutreach || loading || selected.size === 0}
          >
            {sendingOutreach ? 'Sending...' : `Send to Outreach${selected.size ? ` (${selected.size})` : ''}`}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {geminiConfigured === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Gemini API key is not set, so Surf cannot run. Add it in Settings → APIs.
        </Alert>
      )}
      {pageAlert && (
        <Alert severity={pageAlert.severity} sx={{ mb: 2 }} onClose={() => setPageAlert(null)}>
          {pageAlert.message}
        </Alert>
      )}

      {/* Global Controls */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <Typography variant="body2" fontWeight={600}>Add keyword to all queries:</Typography>
        <TextField
          size="small"
          value={globalKeywords}
          onChange={(e) => setGlobalKeywords(e.target.value)}
          placeholder="e.g. cannabis"
          sx={{ width: 260 }}
        />
        <Button
          variant="contained"
          size="small"
          color="secondary"
          startIcon={<KeyboardArrowDownIcon />}
          onClick={handleApplyGlobalKeywords}
        >
          Apply to All Rows
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Flagged Surf results are saved under this list name (defaults to a timestamp)">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlagTriangleIcon sx={{ fontSize: 16, color: 'warning.main' }} />
            <TextField
              size="small"
              label="Save-for-later list"
              value={saveListName}
              onChange={(e) => setSaveListName(e.target.value)}
              sx={{ width: 240 }}
            />
          </Box>
        </Tooltip>

        {/* Deep Search + Done button */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={deepSearch} onChange={(e) => setDeepSearch(e.target.checked)} />}
            label={
              <Tooltip title="Fetch 100 results per Surf query (but show 5); all unique domains captured for later AI evaluation">
                <Typography variant="body2" fontWeight={600}>Deep Search {deepSearchCount > 0 ? `(${deepSearchCount})` : ''}</Typography>
              </Tooltip>
            }
          />
          <Button
            variant="outlined"
            size="small"
            color="success"
            onClick={async () => {
              try {
                const result = await ApiService.markBatchDone(listName);
                if (result.nextList) {
                  navigate(`/outreach/search?list=${encodeURIComponent(result.nextList)}`);
                } else {
                  navigate('/outreach/search');
                }
              } catch (err) {
                console.error('Failed to mark done:', err);
              }
            }}
            disabled={loading || !listName}
          >
            Done with this batch
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: '74vh' }}>
          <Table stickyHeader size="small" sx={{ '& td, & th': { py: 0.5, px: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>Domain</TableCell>
                <TableCell sx={{ minWidth: 90 }}>State</TableCell>
                <TableCell sx={{ minWidth: 220 }}>Search Query</TableCell>
                <TableCell sx={{ minWidth: 230 }}>Tags (Max 4)</TableCell>
                <TableCell sx={{ minWidth: 180 }}>Newsletters</TableCell>
                <TableCell align="center" sx={{ minWidth: 70 }}>
                  <Tooltip title="Contacts for this company currently in the mailer">
                    <span>In&nbsp;Mailer</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">Override</TableCell>
                <TableCell>Status</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    No companies in this list. Use "Send to Search" from the Database page.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => {
                  const itemTags = itemTagObjects(item);
                  const rs = rowSearch[item.id];
                  const expanded = !!rs && (rs.loading || rs.results.length > 0 || !!rs.error);

                  return (
                    <React.Fragment key={item.id}>
                      <TableRow
                        hover
                        selected={selected.has(item.id)}
                        sx={{
                          backgroundColor: item.uncertainty_score > 0.3 ? 'rgba(255, 165, 0, 0.08)' : 'inherit',
                          '& td': { borderBottom: expanded ? 'none' : undefined },
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selected.has(item.id)}
                            onChange={() => toggleRow(item.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {item.website || item.business_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {displayName(item)}
                          </Typography>
                          {item.url && (
                            <Typography variant="caption" color="success.main" noWrap display="block">
                              ✓ {item.url}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>{item.state_name || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ position: 'relative' }}>
                            <TextField
                              size="small"
                              fullWidth
                              variant="standard"
                              value={item.search_query || ''}
                              onChange={(e) => handleUpdateItem(item.id, 'search_query', e.target.value)}
                              onBlur={(e) => saveRowField(item.id, 'search_query', e.target.value)}
                              placeholder="Search terms"
                            />
                            {savingRows.has(item.id) && (
                              <Typography variant="caption" sx={{ color: 'success.main', fontSize: 10 }}>✓</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            multiple
                            size="small"
                            options={Array.isArray(tags) ? tags : []}
                            getOptionLabel={(option) => option.name}
                            value={itemTags}
                            isOptionEqualToValue={(o, v) => o.name === v.name}
                            onChange={(_, newValue) => {
                              if (newValue.length > 4) return;
                              handleUpdateTags(item.id, newValue as Tag[]);
                            }}
                            onBlur={() => {
                              saveRowField(item.id, 'tag1', itemTags[0]?.name || null);
                              saveRowField(item.id, 'tag2', itemTags[1]?.name || null);
                              saveRowField(item.id, 'tag3', itemTags[2]?.name || null);
                              saveRowField(item.id, 'tag4', itemTags[3]?.name || null);
                            }}
                            renderInput={(params) => (
                              <TextField {...params} variant="standard" placeholder={itemTags.length < 4 ? 'Tags...' : ''} />
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip size="small" label={option.name} {...getTagProps({ index })} />
                              ))
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            multiple
                            size="small"
                            options={Array.isArray(NEWSLETTERS) ? NEWSLETTERS : []}
                            value={item.newsletters || []}
                            onChange={(_, newValue) => handleUpdateItem(item.id, 'newsletters', newValue)}
                            onBlur={() => saveRowField(item.id, 'newsletters', item.newsletters)}
                            renderInput={(params) => (
                              <TextField {...params} variant="standard" placeholder={(item.newsletters || []).length ? '' : 'Newsletters...'} />
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip size="small" color="primary" variant="outlined" label={option} {...getTagProps({ index })} />
                              ))
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{item.in_mailer_count ?? 0}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Lock this row from automated changes">
                            <Checkbox
                              size="small"
                              checked={item.human_override}
                              onChange={(e) => {
                                handleUpdateItem(item.id, 'human_override', e.target.checked);
                                saveRowField(item.id, 'human_override', e.target.checked);
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={item.llm_status || 'pending'}
                            color={item.llm_status === 'completed' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell padding="checkbox">
                          <IconButton size="small" color="error" onClick={() => handleDeleteRow(item.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      {/* Accordion: Surf results for this row */}
                      <TableRow>
                        <TableCell colSpan={10} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <SurfResults
                              item={item}
                              rs={rs}
                              tagLabels={itemTags.map(t => t.name)}
                              savedUrls={savedUrls}
                              onToggleSave={(r) => toggleSaveResult(item, r)}
                              onPick={(p) => pickResult(item, p)}
                              onOtherChange={(v) => setOtherUrl(item, v)}
                              onMore={() => runSearchForRow(item, true)}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

// Results panel that accordions down under a row after Surf.
const SurfResults: React.FC<{
  item: SearchListItem;
  rs?: RowSearch;
  tagLabels: string[];
  savedUrls: Set<string>;
  onToggleSave: (r: SearchResult) => void;
  onPick: (picked: number | 'other' | null) => void;
  onOtherChange: (value: string) => void;
  onMore: () => void;
}> = ({ rs, tagLabels, savedUrls, onToggleSave, onPick, onOtherChange, onMore }) => {
  if (!rs) return null;
  const companyTag = tagLabels[0] || '';

  return (
    <Box sx={{ py: 1.5, pl: 6, pr: 2, bgcolor: 'rgba(25,118,210,0.03)' }}>
      {rs.loading && rs.results.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} /> <Typography variant="body2">Searching…</Typography>
        </Box>
      ) : rs.error ? (
        <Typography variant="body2" color="error">{rs.error}</Typography>
      ) : (
        <RadioGroup
          value={rs.picked === null ? '' : String(rs.picked)}
          onChange={(e) => {
            const v = e.target.value;
            onPick(v === 'other' ? 'other' : v === '' ? null : parseInt(v, 10));
          }}
        >
          {rs.results.map((r, i) => {
            const saved = savedUrls.has(r.url);
            return (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.75 }}>
              {/* Triangle: save this web result for later (amber = saved, blue = already in DB) */}
              <Tooltip title={
                saved ? 'Saved for later — click to remove'
                  : r.existsInDb ? 'Already in your database — save anyway?'
                  : 'Save this result for later'
              }>
                <IconButton size="small" sx={{ p: 0.25, mt: 0.25 }} onClick={() => onToggleSave(r)}>
                  <FlagTriangleIcon sx={{
                    fontSize: 15,
                    color: saved ? 'warning.main' : r.existsInDb ? 'info.main' : 'action.disabled',
                  }} />
                </IconButton>
              </Tooltip>
              <FormControlLabel
                value={String(i)}
                control={<Radio size="small" sx={{ py: 0 }} />}
                sx={{ m: 0, alignItems: 'flex-start' }}
                label={
                  <Box sx={{ ml: 0.5 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Link href={r.url} target="_blank" rel="noopener" variant="body2" fontWeight={600} underline="hover">
                        {r.title || r.url}
                      </Link>
                      <OpenInNewIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                      {(r.tag || companyTag) && (
                        <Chip size="small" variant="outlined" label={r.tag || companyTag} sx={{ height: 18, fontSize: 11 }} />
                      )}
                      {r.existsInDb && (
                        <Chip size="small" color="info" variant="outlined" label="in DB" sx={{ height: 18, fontSize: 11 }} />
                      )}
                    </Stack>
                    <Typography variant="caption" color="success.main" display="block">{r.displayLink || r.url}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">{r.description}</Typography>
                  </Box>
                }
              />
            </Box>
          );})}

          {/* None + Other URL */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
            <FormControlLabel value="" control={<Radio size="small" />} label={<Typography variant="body2">None</Typography>} />
            <FormControlLabel
              value="other"
              control={<Radio size="small" />}
              label={
                <TextField
                  size="small"
                  variant="standard"
                  placeholder="Other URL I think works…"
                  value={rs.otherUrl}
                  onChange={(e) => onOtherChange(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ width: 280 }}
                />
              }
            />
            {rs.hasMore && (
              <Button size="small" onClick={onMore} disabled={rs.loading}>
                {rs.loading ? 'Loading…' : 'More results'}
              </Button>
            )}
          </Box>
        </RadioGroup>
      )}
    </Box>
  );
};

export default OutreachSearchPage;
